import React from 'react';
import { Helmet } from 'react-helmet';
import { FileText, Scale, AlertCircle, CheckCircle, XCircle, Mail, Phone } from 'lucide-react';

const TermsConditions = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions - NextGen Filling Association | Legal Terms of Service</title>
        <meta name="description" content="Read our terms and conditions to understand the legal terms governing the use of NextGen Filling Association services and website." />
        <meta name="keywords" content="terms and conditions, terms of service, legal agreement, user agreement, service terms" />
        <link rel="canonical" href="https://nextgenfilling.com/terms-conditions" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Header Section */}
        <section className="bg-gradient-to-r from-slate-900 to-blue-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center mb-4">
              <Scale className="w-16 h-16 text-amber-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Terms & Conditions
            </h1>
            <p className="text-lg text-center text-slate-300 max-w-3xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
              Please read these terms and conditions carefully before using our services.
            </p>
            <p className="text-sm text-center text-slate-400 mt-4">
              Last Updated: February 28, 2025
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
              
              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <div className="flex items-start gap-4 mb-6">
                  <FileText className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      1. Agreement to Terms
                    </h2>
                    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                      These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("Client," "you," or "your") 
                      and NextGen Filling Association ("Company," "we," "us," or "our") regarding your use of our website and services. 
                      By accessing or using our website at nextgenfilling.com or engaging our services, you agree to be bound by these Terms.
                    </p>
                    <p className="text-slate-700 leading-relaxed mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                      If you do not agree to these Terms, you must not access or use our website or services.
                    </p>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  2. Services Provided
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  NextGen Filling Association provides professional services related to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li><strong>Company Registration:</strong> Private Limited Company, LLP, OPC, Proprietorship, Section 8 Company, Nidhi Company</li>
                  <li><strong>Tax Services:</strong> GST Registration, GST Filing, Tax Consultancy</li>
                  <li><strong>Licenses & Certifications:</strong> FSSAI, Trademark, IEC, ESI, PF, PT, MSME, Shop & Establishment, Startup India</li>
                  <li><strong>Compliance Services:</strong> Annual filings, statutory compliance, regulatory compliance</li>
                  <li><strong>Miscellaneous Services:</strong> Director change, address change, MOA amendment, capital increase, and other business services</li>
                </ul>
              </div>

              {/* Eligibility */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  3. Eligibility
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  To use our services, you must:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-3">
                  <li>Be at least 18 years of age</li>
                  <li>Have the legal capacity to enter into a binding contract</li>
                  <li>Provide accurate and complete information</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Not be prohibited from receiving services under Indian law</li>
                </ul>
              </div>

              {/* User Obligations */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  4. User Obligations and Responsibilities
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  When using our services, you agree to:
                </p>
                
                <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.1 Document Submission</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Provide all required documents in a timely manner</li>
                  <li>Ensure all documents and information are genuine, accurate, and complete</li>
                  <li>Inform us immediately of any changes to submitted information</li>
                  <li>Obtain necessary signatures and authorizations from directors/partners</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.2 Compliance</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Comply with all applicable laws, rules, and regulations</li>
                  <li>Not use our services for any illegal or unauthorized purpose</li>
                  <li>Maintain confidentiality of login credentials and account information</li>
                  <li>Pay all applicable fees and charges on time</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.3 Prohibited Activities</h3>
                <p className="text-slate-700 leading-relaxed mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  You agree not to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Provide false, misleading, or fraudulent information</li>
                  <li>Impersonate any person or entity</li>
                  <li>Use our services to engage in money laundering or other illegal activities</li>
                  <li>Interfere with or disrupt our website or services</li>
                  <li>Attempt unauthorized access to our systems</li>
                  <li>Use automated systems to access our website without permission</li>
                </ul>
              </div>

              {/* Fees and Payment */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  5. Fees and Payment Terms
                </h2>
                
                <h3 className="text-xl font-semibold text-slate-800 mt-4 mb-3">5.1 Service Fees</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Service fees are quoted separately for each service based on complexity and requirements</li>
                  <li>Government fees, stamp duty, and other statutory charges are additional</li>
                  <li>All fees are in Indian Rupees (INR) unless otherwise specified</li>
                  <li>Fees are subject to change with prior notice</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.2 Payment Terms</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Payment must be made as per agreed payment schedule</li>
                  <li>We accept bank transfers, UPI, credit/debit cards, and other digital payment methods</li>
                  <li>Work will commence only after receipt of advance payment (if applicable)</li>
                  <li>Late payments may result in suspension of services</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.3 Refund Policy</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Professional fees are non-refundable once work has commenced</li>
                  <li>Government fees and statutory charges are non-refundable</li>
                  <li>Refunds will be processed within 15 working days (if applicable)</li>
                  <li>Refund claims must be raised within 7 days of payment</li>
                </ul>
              </div>

              {/* Service Delivery */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  6. Service Delivery and Timeline
                </h2>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Service timelines are estimates and depend on government processing times</li>
                  <li>Delays by government authorities are beyond our control</li>
                  <li>Timely submission of complete documents by the client is essential</li>
                  <li>We will provide regular updates on application status</li>
                  <li>Additional time may be required for complex cases or queries from authorities</li>
                </ul>
              </div>

              {/* Scope of Services */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  7. Scope and Limitations of Services
                </h2>
                
                <div className="flex items-start gap-3 mb-4 bg-green-50 p-4 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">What We Do</h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-700">
                      <li>Provide professional advisory and consultancy services</li>
                      <li>Prepare and file applications with government authorities</li>
                      <li>Assist with documentation and compliance</li>
                      <li>Communicate with authorities on your behalf</li>
                      <li>Provide guidance on legal and regulatory requirements</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 mb-4 bg-red-50 p-4 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">What We Don't Guarantee</h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-700">
                      <li>Approval of applications by government authorities</li>
                      <li>Specific timelines due to government processing delays</li>
                      <li>Outcomes of inspections or queries by authorities</li>
                      <li>Legal representation in courts or tribunals (unless separately engaged)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Intellectual Property */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  8. Intellectual Property Rights
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  All content on our website, including text, graphics, logos, images, software, and documentation, is the 
                  property of NextGen Filling Association and is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>You may not reproduce, distribute, or modify any content without our written permission</li>
                  <li>Our brand name, logos, and trademarks may not be used without authorization</li>
                  <li>Templates, formats, and documents provided are for your specific use only</li>
                </ul>
              </div>

              {/* Confidentiality */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  9. Confidentiality
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We maintain strict confidentiality of all client information and documents. We will not disclose your 
                  information to third parties except:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-3">
                  <li>When required for service delivery (filing with government authorities)</li>
                  <li>When legally required by law or court order</li>
                  <li>With your explicit consent</li>
                  <li>To our professional advisors under confidentiality obligations</li>
                </ul>
              </div>

              {/* Liability */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  10. Limitation of Liability
                </h2>
                <div className="flex items-start gap-3 mb-4 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                      To the maximum extent permitted by law:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-2">
                      <li>We are not liable for delays or rejections by government authorities</li>
                      <li>We are not responsible for consequences of inaccurate information provided by you</li>
                      <li>Our liability is limited to the fees paid for the specific service</li>
                      <li>We are not liable for indirect, consequential, or special damages</li>
                      <li>We are not responsible for business losses, loss of profits, or opportunity costs</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Indemnification */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  11. Indemnification
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  You agree to indemnify and hold harmless NextGen Filling Association, its employees, and agents from any claims, 
                  losses, damages, or expenses arising from:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-3">
                  <li>Your violation of these Terms</li>
                  <li>Provision of false or misleading information</li>
                  <li>Your violation of applicable laws or regulations</li>
                  <li>Unauthorized use of our services</li>
                </ul>
              </div>

              {/* Termination */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  12. Termination
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Either party may terminate the service agreement:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>By mutual written agreement</li>
                  <li>In case of breach of terms by the other party</li>
                  <li>With 15 days written notice (before work commencement)</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Upon termination, you remain liable for all outstanding fees. Completed work and documents will be provided 
                  after full payment.
                </p>
              </div>

              {/* Dispute Resolution */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  13. Dispute Resolution
                </h2>
                <p className="text-slate-700 leading-relaxed mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  In case of any disputes:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                  <li>Parties will first attempt to resolve through good faith negotiations</li>
                  <li>If unresolved, disputes will be referred to arbitration</li>
                  <li>Arbitration will be conducted in accordance with the Arbitration and Conciliation Act, 1996</li>
                  <li>The seat of arbitration will be [Your City], India</li>
                  <li>The language of arbitration will be English</li>
                </ul>
              </div>

              {/* Governing Law */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  14. Governing Law and Jurisdiction
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  These Terms are governed by the laws of India. Any legal proceedings related to these Terms will be subject 
                  to the exclusive jurisdiction of the courts in [Your City], India.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  15. Modifications to Terms
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting 
                  on our website. Your continued use of our services after changes constitutes acceptance of the modified Terms. 
                  We encourage you to review these Terms periodically.
                </p>
              </div>

              {/* Severability */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  16. Severability
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will 
                  continue in full force and effect.
                </p>
              </div>

              {/* Entire Agreement */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  17. Entire Agreement
                </h2>
                <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  These Terms, together with our Privacy Policy and any service-specific agreements, constitute the entire 
                  agreement between you and NextGen Filling Association regarding the use of our services.
                </p>
              </div>

              {/* Contact Information */}
              <div className="bg-gradient-to-br from-blue-50 to-amber-50 rounded-xl p-8 mt-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  18. Contact Information
                </h2>
                <p className="text-slate-700 leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  If you have any questions about these Terms and Conditions, please contact us:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-semibold text-slate-900">Email</p>
                      <a href="mailto:info@nextgenfilling.com" className="text-blue-600 hover:text-blue-800">
                        info@nextgenfilling.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-semibold text-slate-900">Phone</p>
                      <p className="text-slate-700">+91 9509363261, +91 9541709908</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Acceptance */}
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-6">
                <p className="text-slate-800 font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <strong>Acceptance of Terms:</strong> By accessing our website, using our services, or clicking "I Agree" 
                  on any service agreement, you acknowledge that you have read, understood, and agree to be bound by these 
                  Terms and Conditions.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TermsConditions;
